import * as Sentry from "@sentry/react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Badge from "../../../components/Badge";
import Button from "../../../components/Button";
import Icon from "../../../components/Icon";
import { useToast } from "../../../contexts/ToastContext";
import realtimeClient from "../../../realtime/client";
import {
    RECORDER_STATE,
} from "./constants";
import RecorderDdxSection from "./ddx/RecorderDdxSection";
import { useRecorderLive, useRecorderStable } from "./RecorderContext";
import useAnimatedEllipsis from "./useAnimatedEllipsis";
import { SUMMARY_FAILURE_MESSAGE } from "./useAudioRecorder";
import {
    formatElapsedTime,
} from "./utils";

function getLoadingLabelBase(text) {
    return String(text || "").replace(/\.+$/, "");
}

function renderAnimatedLabel(text, dots, { dotsWidthClass = "w-[1.5rem]" } = {}) {
    return (
        <span className="inline-flex items-center">
            <span>{getLoadingLabelBase(text)}</span>
            <span className={`inline-block ${dotsWidthClass} text-left`}>{dots}</span>
        </span>
    );
}

const SECTION_CLASSNAME_BY_VARIANT = {
    default: "sticky top-0 overflow-hidden rounded-xl border border-main-border-system-default bg-main-background-system-brand font-ui",
    docked: "overflow-hidden rounded-xl border border-main-border-system-default bg-main-background-system-brand font-ui transition-[border-radius,box-shadow] duration-300",
    floating: "overflow-hidden rounded-xl border border-main-border-system-default bg-main-background-system-brand font-ui shadow-[0_12px_32px_rgba(15,23,42,0.18)] transition-[border-radius,box-shadow] duration-300",
    placeholder: "overflow-hidden rounded-xl border border-main-border-system-default bg-main-background-system-brand font-ui invisible pointer-events-none",
};

export default function RecorderSection({
    initialTreatmentDdx = null,
    sectionId = "presscribe-recorder",
    variant = "default",
}) {
    const {
        activeTreatmentId,
        handleContinueRecording,
        handlePauseOrResumeRecording,
        handleRestartRecording,
        handleStopRecording,
        handleSummarizeAgain,
        latestSummaryResult,
        openSummarySettingsModal,
        recorderState,
        updateDockedRect,
    } = useRecorderStable();
    const { recorderErrorMessage } = useRecorderLive();
    const { showErrorToast } = useToast();
    const sectionRef = useRef(null);
    const lastToastedErrorRef = useRef("");
    const [isDiagnosisExpanded, setIsDiagnosisExpanded] = useState(true);
    const [treatmentDdx, setTreatmentDdx] = useState(initialTreatmentDdx);
    const loadingDots = useAnimatedEllipsis(
        recorderState === RECORDER_STATE.INITIALIZING || recorderState === RECORDER_STATE.SUMMARIZING
    );

    const sectionClassName = SECTION_CLASSNAME_BY_VARIANT[variant] || SECTION_CLASSNAME_BY_VARIANT.default;
    const shouldShowDiagnosisSection = variant !== "floating";
    const currentTreatmentId = shouldShowDiagnosisSection
        ? activeTreatmentId || latestSummaryResult?.treatmentId
        : "";

    useLayoutEffect(() => {
        if (variant === "floating" || variant === "placeholder") {
            return undefined;
        }

        const sectionElement = sectionRef.current;
        if (!sectionElement) {
            return undefined;
        }

        function syncDockedRect() {
            const rect = sectionElement.getBoundingClientRect();
            updateDockedRect({
                height: rect.height,
                width: rect.width,
                x: rect.left,
                y: rect.top,
            });
        }

        syncDockedRect();

        const resizeObserver = new ResizeObserver(syncDockedRect);
        resizeObserver.observe(sectionElement);
        window.addEventListener("resize", syncDockedRect);
        window.addEventListener("scroll", syncDockedRect, true);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", syncDockedRect);
            window.removeEventListener("scroll", syncDockedRect, true);
        };
    }, [updateDockedRect, variant]);

    useEffect(() => {
        const normalizedErrorMessage = String(recorderErrorMessage || "").trim();
        if (!normalizedErrorMessage) {
            lastToastedErrorRef.current = "";
            return;
        }

        if (lastToastedErrorRef.current === normalizedErrorMessage) {
            return;
        }

        lastToastedErrorRef.current = normalizedErrorMessage;
        showErrorToast(normalizedErrorMessage);
    }, [recorderErrorMessage, showErrorToast]);

    useEffect(() => {
        setTreatmentDdx(initialTreatmentDdx);

        if (!currentTreatmentId) {
            return undefined;
        }

        function handleTreatmentDdxUpdated(payload = {}) {
            const eventData = payload?.data ?? {};
            if (String(eventData?.treatment_id || "") !== String(currentTreatmentId)) {
                return;
            }

            setTreatmentDdx(eventData?.ddx || null);
        }

        const unsubscribe = realtimeClient.subscribe(
            "treatment_ddx_updated",
            handleTreatmentDdxUpdated,
            { emitCachedPayload: false }
        );

        return () => {
            unsubscribe();
        };
    }, [currentTreatmentId, initialTreatmentDdx]);

    async function executeRecorderAction(action) {
        try {
            await action?.();
        } catch (error) {
            Sentry.withScope((scope) => {
                scope.setTag("feature", "presscribe");
                scope.setLevel("error");
                scope.setContext("presscribe", {
                    actionName: action?.name || "anonymous",
                    errorDetail: String(error?.detail || "").trim(),
                    errorMessage: String(error?.message || "").trim(),
                    errorName: String(error?.name || "").trim(),
                });
                Sentry.captureException(
                    error instanceof Error
                        ? error
                        : new Error("Presscribe recorder action failed")
                );
            });
            showErrorToast(SUMMARY_FAILURE_MESSAGE);
        }
    }

    function handleCompletedActionShortcut() {
        openSummarySettingsModal(true);
    }

    function buildStatusBadge() {
        return <RecorderStatusBadge recorderState={recorderState} />;
    }

    function buildHeader() {
        return (
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2">
                    <RecorderAudioLevelDot recorderState={recorderState} />
                    <Icon name="lightning-bolt" size="md" className="text-main-icon-system-inverted" />
                    <p className="text-button-base font-semibold text-main-typography-system-inverted">ผู้ช่วย</p>
                </div>
                <div className="flex items-center gap-2">
                    {buildStatusBadge()}
                    {shouldShowDiagnosisSection ? (
                        <button
                            type="button"
                            className="inline-flex h-scale-md w-scale-md shrink-0 items-center justify-center text-creation-primary-solid-on-surface-default"
                            aria-label={isDiagnosisExpanded ? "ย่อการวินิจฉัยด้วย AI" : "ขยายการวินิจฉัยด้วย AI"}
                            aria-expanded={isDiagnosisExpanded}
                            onClick={() => setIsDiagnosisExpanded((current) => !current)}
                        >
                            <Icon
                                name={isDiagnosisExpanded ? "chevron-up" : "chevron-down"}
                                size="md"
                            />
                        </button>
                    ) : null}
                </div>
            </div>
        );
    }

    function buildTimerSection() {
        return <RecorderTimer recorderState={recorderState} />;
    }

    function buildActionButtons() {
        if (recorderState === RECORDER_STATE.SUMMARIZING) {
            return buildSummarizingActionButton();
        }

        if (recorderState === RECORDER_STATE.COMPLETED) {
            return buildCompletedActionButtons();
        }

        const isBusy = recorderState === RECORDER_STATE.INITIALIZING;
        const isPauseDisabled = !(recorderState === RECORDER_STATE.RECORDING || recorderState === RECORDER_STATE.PAUSED);
        const isStopDisabled = !(recorderState === RECORDER_STATE.RECORDING || recorderState === RECORDER_STATE.PAUSED);

        return (
            <div className="flex items-stretch gap-3">
                {buildRestartButton(isBusy)}
                {buildPauseButton(isPauseDisabled)}
                {buildSummaryButton(isStopDisabled)}
            </div>
        );
    }

    function buildCompletedActionButtons() {
        return (
            <div className="flex items-stretch gap-3">
                <Button
                    hierarchy="secondary"
                    size="sm"
                    icon="adjustments"
                    onClick={handleCompletedActionShortcut}
                />
                <Button
                    hierarchy="secondary"
                    size="sm"
                    leadingIcon="play"
                    text="บันทึกเสียงต่อ"
                    className="flex-1"
                    onClick={() => void executeRecorderAction(handleContinueRecording)}
                />
                <Button
                    hierarchy="secondary"
                    size="sm"
                    text="สรุป"
                    onClick={() => void executeRecorderAction(handleSummarizeAgain)}
                />
            </div>
        );
    }

    function buildSummarizingActionButton() {
        return (
            <button
                type="button"
                className="btn btn-secondary btn-sm w-full gap-1"
                disabled={true}
            >
                <Icon
                    name="refresh"
                    size="sm"
                    className="animate-spin"
                />
                <span className="inline-flex items-center px-2">
                    <span>กำลังแปลงเสียงเป็นข้อความ</span>
                    <span className="inline-block w-[1.5rem] text-left">{loadingDots}</span>
                </span>
            </button>
        );
    }

    function buildRestartButton(disabled) {
        return (
            <Button
                hierarchy="secondary"
                size="sm"
                icon="refresh"
                disabled={disabled}
                onClick={() => void executeRecorderAction(handleRestartRecording)}
            />
        );
    }

    function buildPauseButton(disabled) {
        const isPaused = recorderState === RECORDER_STATE.PAUSED;

        return (
            <Button
                hierarchy="secondary"
                size="sm"
                leadingIcon={isPaused ? "play" : "pause"}
                text={isPaused ? "บันทึกเสียงต่อ" : "หยุดชั่วคราว"}
                className="flex-1"
                disabled={disabled}
                onClick={() => void executeRecorderAction(handlePauseOrResumeRecording)}
            />
        );
    }

    function buildSummaryButton(disabled) {
        return (
            <Button
                hierarchy="secondary"
                size="sm"
                text="สรุป"
                disabled={disabled}
                className="w-[80px]"
                onClick={() => void executeRecorderAction(handleStopRecording)}
            />
        );
    }

    return (
        <section
            ref={sectionRef}
            id={sectionId}
            className={sectionClassName}
        >
            <div className="flex flex-col gap-5 py-5 pl-5 pr-4">
                {buildHeader()}
                <div className="flex flex-col gap-3">
                    {buildTimerSection()}
                    {buildActionButtons()}
                </div>
            </div>
            {shouldShowDiagnosisSection && isDiagnosisExpanded ? (
                <RecorderDdxSection
                    initialTreatmentDdx={initialTreatmentDdx}
                    isLoading={recorderState === RECORDER_STATE.SUMMARIZING}
                    treatmentDdx={treatmentDdx}
                />
            ) : null}
        </section>
    );
}

function getRecorderIndicatorClassName(recorderState, audioIntensity, recorderErrorMessage) {
    if (recorderState === RECORDER_STATE.SUMMARIZING || recorderState === RECORDER_STATE.COMPLETED) {
        return "";
    }

    if (recorderState === RECORDER_STATE.ERROR || recorderErrorMessage) {
        return "bg-main-icon-accented-error";
    }

    if (recorderState === RECORDER_STATE.PAUSED) {
        return "bg-main-icon-accented-warning";
    }

    if (recorderState === RECORDER_STATE.RECORDING) {
        return audioIntensity === "silent"
            ? "bg-main-icon-accented-warning"
            : "bg-main-icon-accented-success";
    }

    return "";
}

function RecorderAudioLevelDot({ recorderState }) {
    const { audioIntensity, recorderErrorMessage } = useRecorderLive();
    const indicatorClassName = getRecorderIndicatorClassName(
        recorderState,
        audioIntensity,
        recorderErrorMessage
    );

    if (!indicatorClassName) {
        return null;
    }

    return (
        <span
            aria-hidden="true"
            className={`h-4 w-4 shrink-0 rounded-full ${indicatorClassName} transition-colors duration-75`}
            data-audio-intensity={audioIntensity}
            data-recorder-state={recorderState}
        />
    );
}

function RecorderStatusBadge({ recorderState }) {
    const { recorderStatusMeta } = useRecorderLive();
    const loadingDots = useAnimatedEllipsis(
        recorderState === RECORDER_STATE.INITIALIZING || recorderState === RECORDER_STATE.SUMMARIZING
    );
    const listeningDots = useAnimatedEllipsis(
        recorderState === RECORDER_STATE.RECORDING && recorderStatusMeta.text.startsWith("กำลังฟัง")
    );

    const shouldAnimateBadge = (
        recorderState === RECORDER_STATE.INITIALIZING
        || recorderState === RECORDER_STATE.SUMMARIZING
        || (
            recorderState === RECORDER_STATE.RECORDING
            && recorderStatusMeta.text.startsWith("กำลังฟัง")
        )
    )
        && recorderStatusMeta.text;
    const badgeDots = recorderState === RECORDER_STATE.RECORDING ? listeningDots : loadingDots;

    return (
        <Badge
            size="sm"
            color={recorderStatusMeta.color}
            text={shouldAnimateBadge ? "" : recorderStatusMeta.text}
            extraClasses={`rounded-full ${shouldAnimateBadge ? "min-w-[104px]" : ""}`}
        >
            {shouldAnimateBadge
                ? renderAnimatedLabel(recorderStatusMeta.text, badgeDots, {
                    dotsWidthClass: "w-[1.25rem]",
                })
                : null}
        </Badge>
    );
}

function RecorderTimer({ recorderState }) {
    const { elapsedSeconds } = useRecorderLive();
    const isRecording = recorderState === RECORDER_STATE.RECORDING;
    const [minutes = "00", seconds = "00"] = formatElapsedTime(elapsedSeconds).split(":");

    return (
        <div className="flex justify-between items-end">
            <p className="text-body-base text-main-typography-system-inverted">ระยะเวลา</p>
            <div className="flex items-end gap-3 text-white">
                <p className="text-button-base text-main-typography-system-inverted">
                    <span>{minutes}</span>
                    <span
                        className={`inline-block w-[0.65ch] text-center ${isRecording ? "animate-pulse" : ""}`}
                        style={isRecording ? { animationDuration: "0.5s" } : undefined}
                    >
                        :
                    </span>
                    <span>{seconds}</span>
                </p>
                <p className="text-body-base text-main-typography-system-inverted">นาที</p>
            </div>
        </div>
    );
}
